import cn from 'classnames';
import { format } from 'date-fns';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { IconButton } from 'components';
import Avatar from 'components/Avatar';
import { ButtonV3 } from 'components/ComponentV2';
import Modal from 'components/Modal';
import PDF from 'components/PDF';

import { useChatRoom } from 'hooks/useChatRoom';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';

import {
  AnswerAttributes,
  QuestionAttributes,
  QuestionState,
} from 'lib/models/answers';
import { ApplicationAttributes } from 'lib/models/application';
import { DegreeAttributes } from 'lib/models/degree';
import { OpportunityAttributes } from 'lib/models/opportunity';
import { SubjectAttributes } from 'lib/models/subject';
import { UniversityAttributes } from 'lib/models/university';
import { EducationHistoryAttributes } from 'lib/models/candidate';
import { formatDate } from 'utils/format';

import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from './GuestModal.module.scss';

interface RegistrationSettingModalProps {
  isOpen: boolean;
  onClose: () => void;
  applicant: ApplicationAttributes | null;
  touchpoint: OpportunityAttributes;
}

export const GuestModal = ({
  isOpen,
  onClose,
  applicant,
  touchpoint,
}: RegistrationSettingModalProps): ReactElement => {
  const { onRejectApplications, onReleaseTickets } = useAttendeesFilter();

  const [t] = useTranslation('event-dashboard');

  const {
    id,
    name,
    resume_url,
    avatar_url,
    linkedin_url,
    education_histories,
    answers,
    candidate_id,
    waitlist,
    applied_at,
  } = applicant || {};

  const {
    id: touchpointId,
    cv_required,
    cv_required_state,
    study_year_required,
    study_year_required_state,
  } = touchpoint || {};

  const educationHistories =
    parseArrayResponse<EducationHistoryAttributes>(education_histories);
  const educationAttributes = educationHistories[educationHistories.length - 1];

  const { university, degree, subject, study_year, graduationYear } =
    useMemo(() => {
      const university = parseResponse<UniversityAttributes>(
        educationAttributes?.university
      )?.name;
      const degree = parseResponse<DegreeAttributes>(
        educationAttributes?.degree
      )?.name;
      const subject = parseResponse<SubjectAttributes>(
        educationAttributes?.subject
      )?.name;
      const study_year = educationAttributes?.current_study_year;
      const graduationYear = educationAttributes?.end_date
        ? formatDate(new Date(educationAttributes.end_date))
        : '';

      return {
        university,
        degree,
        subject,
        study_year,
        graduationYear,
      };
    }, [educationAttributes]);

  const { createMessageChat } = useChatRoom();

  const basicInfo = [
    ...(university
      ? [
          {
            id: 1,
            label: t('guest-detail-modal.basic-info.university'),
            value: university,
          },
        ]
      : []),
    ...(study_year
      ? [
          {
            id: 2,
            label: t('guest-detail-modal.basic-info.study-year'),
            value: study_year,
          },
        ]
      : []),
    ...(degree
      ? [
          {
            id: 3,
            label: t('guest-detail-modal.basic-info.degree-type'),
            value: degree,
          },
        ]
      : []),
    ...(subject
      ? [
          {
            id: 4,
            label: t('guest-detail-modal.basic-info.subject-of-study'),
            value: subject,
          },
        ]
      : []),
  ];

  const answersAttributes = parseArrayResponse<AnswerAttributes>(answers);

  const customQuestions = answersAttributes.map(({ id, question, value }) => {
    const questionAttributes = parseResponse<QuestionAttributes>(question);
    return {
      id,
      label: questionAttributes?.title,
      value,
      questionType: questionAttributes?.question_type,
    };
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentClassName={styles.container}
    >
      <div className={styles.cancelIcon}>
        <IconButton size="small" iconName="close" onClick={() => onClose()} />
      </div>
      <div className={styles.body}>
        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.guestDetails}>
              <Avatar
                icon={avatar_url}
                name={name || ''}
                altText={name}
                size="xlarge"
              />
              <div className={styles.guestDetailsNameWrapper}>
                <p className={styles.name}>{name}</p>
                {!waitlist && applied_at && (
                  <div>
                    <span className={styles.badge}>
                      {t('guest-detail-modal.misc.accepted')}
                    </span>
                    {t('guest-detail-modal.misc.since', {
                      date: format(new Date(applied_at), 'MMM d'),
                    })}
                  </div>
                )}
              </div>
            </div>
            <div>
              {linkedin_url && (
                <IconButton
                  iconName="linkedin"
                  onClick={() => {
                    window.open(linkedin_url, '_blank');
                  }}
                  className={styles.iconButton}
                />
              )}
              {candidate_id && (
                <IconButton
                  iconName="icon_mail"
                  onClick={() => createMessageChat(candidate_id)}
                  className={styles.iconButton}
                />
              )}
            </div>
          </div>
          {basicInfo.length ? (
            <div className={cn(styles.row, styles.basicInfo)}>
              <div className={styles.basicInfoTitle}>
                {t('guest-detail-modal.basic-info.title')}
              </div>
              <div className={styles.basicInfoBox}>
                {basicInfo.map(({ id, label, value }) => (
                  <div key={id} className={styles.infoItem}>
                    <span className={styles.label}>{label}</span>
                    <span className={styles.fieldValue}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <></>
          )}
          {(study_year_required ||
            study_year_required_state !== QuestionState.OFF) &&
            graduationYear && (
              <div className={cn(styles.row, styles.customQuestion)}>
                <div className={styles.customQuestionTitle}>
                  {t('guest-detail-modal.graduation-year')}
                </div>
                <div className={styles.answerWrapper}>
                  <div className={cn(styles.answerBox)}>{graduationYear}</div>
                </div>
              </div>
            )}
          {customQuestions.length > 0 &&
            customQuestions.map(({ id, label, value, questionType }) => {
              return (
                <div key={id} className={cn(styles.row, styles.customQuestion)}>
                  <div className={styles.customQuestionTitle}>{label}</div>
                  <div className={styles.answerWrapper}>
                    {questionType === 'multiple_choice' ? (
                      value.split(', ').map((val, index) => (
                        <div key={index} className={styles.answerBox}>
                          {val}
                        </div>
                      ))
                    ) : (
                      <pre
                        className={cn(styles.answerBox, {
                          [styles.paragraph]: [
                            'short_answer',
                            'paragraph',
                          ].includes(questionType),
                        })}
                      >
                        {value}
                      </pre>
                    )}
                  </div>
                </div>
              );
            })}
          {(cv_required || cv_required_state !== QuestionState.OFF) &&
            resume_url && (
              <div className={cn(styles.row, styles.customQuestion)}>
                <div className={styles.customQuestionTitle}>CV</div>
                <PDF
                  url={resume_url}
                  className={styles.attendeeProfileCVPreview}
                />
              </div>
            )}
        </div>
      </div>
      {waitlist && id && (
        <div className={styles.footer}>
          <div className={styles.separator}></div>
          <div className={styles.section}>
            <ButtonV3
              onClick={async () => {
                await onReleaseTickets({
                  touchpointId,
                  touchpoint_application_ids: [id],
                });
                onClose();
              }}
              color="primary"
              startIcon="icon_check"
            >
              {t('common.approve')}
            </ButtonV3>

            <ButtonV3
              onClick={async () => {
                await onRejectApplications([id]);
                onClose();
              }}
              color="secondary"
              startIcon="icon_close"
            >
              {t('common.decline')}
            </ButtonV3>
          </div>
        </div>
      )}
    </Modal>
  );
};
